/**
 * "Car events near you" — host dashboard widget fed by the api-server
 * proxy `GET /api/host/events-nearby` (partner events API, keys stay
 * server-side).
 *
 * Hide-on-anything-imperfect: the section renders NOTHING while the
 * feature flag is off, on any fetch error, or when the server returns
 * an empty list (upstream failure, no host location, or too few quality
 * events). Event links open the partner page in a new tab (URLs already
 * carry UTM tracking).
 *
 * Loading: to avoid a skeleton that flashes then vanishes for the many
 * hosts with no nearby events, we only show a skeleton while fetching
 * when a sessionStorage hint says this host saw events last time (the
 * hint also remembers how many, so the skeleton matches the featured
 * vs grid layout and there is no reflow on settle). First-ever visits
 * keep the old behavior: render nothing until real events arrive.
 */
import { useEffect, useState } from "react";
import { Calendar, ExternalLink, MapPin } from "lucide-react";
import { useFeatureFlag } from "@/hooks/useFeatureFlags";
import { useAppTranslation } from "@/i18n/useAppTranslation";
import { useUser } from "@/hooks/useUser";
import { getDistanceUnit } from "@/lib/api";

export interface NearbyCarEvent {
  id: string;
  title: string;
  startDate: string;
  city: string | null;
  country: string | null;
  category: string | null;
  url: string;
  imageUrl: string;
  distanceKm: number | null;
}

const API_BASE = `${import.meta.env.BASE_URL || "/"}api`;

const SUBMIT_EVENT_URL =
  "https://events.driveshare.com/submit?utm_source=bookaclassic&utm_medium=host-dashboard";

interface NearbyEventsResponse {
  events?: NearbyCarEvent[];
  originCountryCode?: string | null;
}

function distanceFromKm(distanceKm: number, originCountryCode: string | null): { value: number; unit: "mile" | "km" } {
  // The general listing helper defaults unknown countries to miles; this
  // geographic distance has no such evidence, so unknown means km.
  const unit = originCountryCode ? getDistanceUnit(originCountryCode) : "km";
  return { value: Math.round(unit === "mile" ? distanceKm / 1.609344 : distanceKm), unit };
}

async function fetchNearbyCarEvents(token: string): Promise<NearbyEventsResponse> {
  const res = await fetch(`${API_BASE}/host/events-nearby`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return { events: [] };
  const body = (await res.json().catch(() => null)) as NearbyEventsResponse | null;
  return {
    events: Array.isArray(body?.events) ? body.events : [],
    originCountryCode: typeof body?.originCountryCode === "string" ? body.originCountryCode : null,
  };
}

/**
 * sessionStorage hint: how many events this host saw on their last visit.
 * Guarded — storage can throw (private mode, in-app webviews); treat any
 * failure as "no hint" so the widget just skips the skeleton.
 */
const LAST_SEEN_KEY_PREFIX = "bac_car_events_last_count:";

function readLastSeenCount(userId: string): number {
  try {
    const n = Number(sessionStorage.getItem(LAST_SEEN_KEY_PREFIX + userId));
    return Number.isFinite(n) && n > 0 ? Math.min(n, 6) : 0;
  } catch {
    return 0;
  }
}

function writeLastSeenCount(userId: string, count: number): void {
  try {
    sessionStorage.setItem(LAST_SEEN_KEY_PREFIX + userId, String(count));
  } catch {
    // ignore — hint only
  }
}

/** Pulsing placeholder mirroring the featured (count===1) or grid layout. */
function CarEventsSkeleton({ count, title }: { count: number; title: string }) {
  return (
    <section data-testid="dashboard-car-events-skeleton" aria-busy="true">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 className="font-sans text-xl font-bold text-brand">{title}</h2>
      </div>
      {count === 1 ? (
        <div className="mt-4 flex animate-pulse flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-sm sm:flex-row">
          <div className="aspect-[16/10] bg-muted sm:aspect-auto sm:min-h-[180px] sm:w-1/2 lg:w-2/5" />
          <div className="flex flex-1 flex-col justify-center gap-3 p-5 sm:p-6">
            <div className="h-4 w-20 rounded-full bg-muted" />
            <div className="h-6 w-3/4 rounded bg-muted" />
            <div className="h-4 w-1/2 rounded bg-muted" />
          </div>
        </div>
      ) : (
        <div className="mt-4 grid animate-pulse gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: Math.min(count, 3) }).map((_, i) => (
            <div key={i} className="overflow-hidden rounded-2xl border border-border bg-background shadow-sm">
              <div className="aspect-[16/10] bg-muted" />
              <div className="space-y-2 p-4">
                <div className="h-4 w-16 rounded-full bg-muted" />
                <div className="h-5 w-3/4 rounded bg-muted" />
                <div className="h-3.5 w-1/2 rounded bg-muted" />
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export function DashboardCarEvents() {
  const { t, i18n } = useAppTranslation();
  const enabled = useFeatureFlag("feature_car_events_widget");
  const { session } = useUser();
  const token = session?.access_token;
  const userId = session?.user?.id ?? null;

  // Plain fetch (no react-query) so the widget carries zero provider
  // dependencies — it must be safely mountable anywhere on the dashboard.
  // Results are BOUND to the user id that fetched them so an in-place
  // account switch can never paint the previous host's events — a stale
  // `forUser` renders as loading, not as old data.
  const [result, setResult] = useState<{ forUser: string; list: NearbyCarEvent[]; originCountryCode: string | null } | null>(null);
  useEffect(() => {
    if (!enabled || !token || !userId) return;
    let cancelled = false;
    fetchNearbyCarEvents(token)
      .then(({ events: list = [], originCountryCode = null }) => {
        if (cancelled) return;
        setResult({ forUser: userId, list, originCountryCode });
        writeLastSeenCount(userId, list.length);
      })
      .catch(() => {
        if (!cancelled) setResult({ forUser: userId, list: [], originCountryCode: null }); // fail soft — widget hides
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, token, userId]);
  if (!enabled || !userId) return null;
  const events = result && result.forUser === userId ? result.list : null;
  if (events === null) {
    // Loading: skeleton only when THIS host saw events recently, sized to
    // the layout they saw (featured vs grid) so settling doesn't reflow.
    const lastCount = readLastSeenCount(userId);
    if (lastCount < 1) return null;
    return <CarEventsSkeleton count={lastCount} title={t("dashboard.carEvents.title", "Car events near you")} />;
  }
  if (events.length === 0) return null;

  const formatDistance = (km: number) => {
    const { value, unit } = distanceFromKm(km, result?.originCountryCode ?? null);
    return unit === "mile"
      ? t("dashboard.carEvents.distanceMiles", "{{miles}} miles away", { miles: value })
      : t("dashboard.carEvents.distance", "{{km}} km away", { km: value });
  };

  const fmtDate = (iso: string) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(i18n.language, { weekday: "short", day: "numeric", month: "short" });
  };

  return (
    <section data-testid="dashboard-car-events">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 className="font-sans text-xl font-bold text-brand">
          {t("dashboard.carEvents.title", "Car events near you")}
        </h2>
        <a
          href={SUBMIT_EVENT_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="car-events-submit-link"
          className="text-sm text-[#5a6b5a] underline-offset-2 hover:text-brand hover:underline"
        >
          {t("dashboard.carEvents.submit", "Tell us about your classic car event")}
          <ExternalLink className="ml-1 inline h-3 w-3 align-[-1px]" />
        </a>
      </div>

      {events.length === 1 ? (
        // Featured layout: a lone nearby event fills the row as a
        // hero-style card instead of a lonely third-width tile.
        (() => {
          const event = events[0];
          return (
            <a
              href={event.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`car-event-featured-${event.id}`}
              className="group mt-4 flex flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-sm transition hover:shadow-md sm:flex-row"
            >
              <div className="aspect-[16/10] overflow-hidden bg-muted sm:aspect-auto sm:w-1/2 lg:w-2/5">
                <img
                  src={event.imageUrl}
                  alt=""
                  loading="lazy"
                  className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.visibility = "hidden";
                  }}
                />
              </div>
              <div className="flex flex-1 flex-col justify-center p-5 sm:p-6">
                {event.category && (
                  <span className="inline-block w-fit rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-[#5a6b5a]">
                    {event.category}
                  </span>
                )}
                <p className="mt-2 line-clamp-2 text-lg font-semibold text-brand group-hover:underline sm:text-xl">
                  {event.title}
                  <ExternalLink className="ml-1 inline h-4 w-4 align-[-2px] text-[#5a6b5a]" />
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#5a6b5a]">
                  <span className="inline-flex items-center gap-1.5">
                    <Calendar className="h-4 w-4" />
                    {fmtDate(event.startDate)}
                  </span>
                  {(event.city || event.distanceKm != null) && (
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin className="h-4 w-4" />
                      {event.city ?? ""}
                      {event.city && event.distanceKm != null ? " · " : ""}
                      {event.distanceKm != null
                         ? formatDistance(event.distanceKm)
                        : ""}
                    </span>
                  )}
                </div>
              </div>
            </a>
          );
        })()
      ) : (
      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {events.map((event) => (
          <a
            key={event.id}
            href={event.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`car-event-card-${event.id}`}
            className="group overflow-hidden rounded-2xl border border-border bg-background shadow-sm transition hover:shadow-md"
          >
            <div className="aspect-[16/10] overflow-hidden bg-muted">
              <img
                src={event.imageUrl}
                alt=""
                loading="lazy"
                className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.visibility = "hidden";
                }}
              />
            </div>
            <div className="p-4">
              {event.category && (
                <span className="inline-block rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-[#5a6b5a]">
                  {event.category}
                </span>
              )}
              <p className="mt-1.5 line-clamp-2 font-semibold text-brand group-hover:underline">
                {event.title}
                <ExternalLink className="ml-1 inline h-3.5 w-3.5 align-[-2px] text-[#5a6b5a]" />
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#5a6b5a]">
                <span className="inline-flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  {fmtDate(event.startDate)}
                </span>
                {(event.city || event.distanceKm != null) && (
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5" />
                    {event.city ?? ""}
                    {event.city && event.distanceKm != null ? " · " : ""}
                    {event.distanceKm != null
                       ? formatDistance(event.distanceKm)
                      : ""}
                  </span>
                )}
              </div>
            </div>
          </a>
        ))}
      </div>
      )}
    </section>
  );
}
